import React, { useState, useEffect, memo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Button, Icon } from '@iq/react-components';
import ListItem from '../../ListItem';
import {
  saveStateSet,
  getStateSets,
  deleteStateSet,
  updateStateSet,
} from '../../../bundles/statesets';

import Heading from '../../Heading';
import SimpleModal from '../../SimpleModal/SimpleModal';
import { schema, uiSchema } from './schemas/stateSetSchema';
import JSONEditor from '../../JSONEditor';
import { getActiveSite } from '../../../bundles/sites';

const StateSetView = memo(() => {
  const [showModal, setShowModal] = useState(false);
  const dispatch = useDispatch();

  const [formData, setFormData] = useState({});

  const { id: siteId, org } = useSelector(getActiveSite);
  const stateSets = useSelector(getStateSets);

  const customTransformErrors = (errors) =>
    errors.filter((error) => error.name !== 'if' && error.name !== 'type');

  const onFormChange = ({ formData: updatedFormData }) => {
    setFormData({ ...updatedFormData });
  };

  const handleUpdateState = (data) => {
    setShowModal(true);
    setFormData(data);
  };

  const onCancel = () => {
    setShowModal(false);
    setFormData({});
  };

  const onDelete = (stateSetId) => {
    dispatch(deleteStateSet(stateSetId, siteId));
  };

  const onSubmit = () => {
    const payload = { ...formData };
    if (payload.id) {
      dispatch(updateStateSet(payload));
      onCancel();
    } else {
      dispatch(saveStateSet(payload, siteId, org));
      onCancel();
    }
  };

  useEffect(() => {
    if (formData.isVirtualStateSet && Array.isArray(formData.states)) {
      setFormData((prevData) => ({ ...prevData, states: {} }));
    }
  }, [formData.isVirtualStateSet]);

  const headers = ['Name', 'Type', 'No. of states', 'Text', 'Color', 'Trigger', 'Suggestion'];

  const headerCols = headers.map((h) => (
    <div
      key={h}
      className="list-item--header__col"
    >
      {h.indexOf('Type') > -1 ? (
        <>
          <div className="stateset-type-info-trigger">
            {h}
            <Icon icon="he-help" />
          </div>
          <div className="stateset-type-info-tooltip--outer">
            <div className="stateset-type-info-tooltip--inner">
              <p>Stateset type cannot be changed after creation</p>
            </div>
          </div>
        </>
      ) : (
        h
      )}
    </div>
  ));

  const getStates = (stateSet) => {
    if (!stateSet.states) {
      return null;
    }
    const states = Array.isArray(stateSet.states) ? stateSet.states : [stateSet.states];

    return states.map((state, index) => [
      '',
      '',
      <div
        key={`state-name-${stateSet.id}-${index}`}
        className="state-name"
      >
        {state.name}
      </div>,
      <div
        key={`state-text-${stateSet.id}-${index}`}
        className="state-text"
      >
        {state.text}
      </div>,
      <div key={`state-color-${stateSet.id}-${index}`}>
        <div
          className="state-color"
          style={{ backgroundColor: state.color }}
        ></div>
        {state.color}
      </div>,
      <div key={`state-trigger-${stateSet.id}-${index}`}>
        {state.trigger?.condition !== '<>' && state.trigger?.triggerValue
          ? `${state.trigger.condition} ${state.trigger.triggerValue}`
          : 'Manual'}
      </div>,
      <div
        key={`state-description-${stateSet.id}-${index}`}
        className="state-description"
      >
        {state.description}
      </div>,
    ]);
  };

  return (
    <>
      <Heading contentRight={<Button onClick={() => setShowModal(true)}>Add state set</Button>} />

      <div className="manage-states-body">
        <div className="list-container">
          <ListItem
            isHeader
            columns={headerCols}
          />
          <div className="custom-thin-scrollbar">
            {stateSets?.map((s, i) => (
              <ListItem
                key={s.name + i}
                itemIndex={i}
                customEdit={true}
                onEdit={handleUpdateState}
                entity={`State set (${s.name})`}
                item={s}
                schema={schema}
                onDelete={() => onDelete(s.id)}
                confirmationDialogTitle={'Delete stateSet'}
                confimationDialogBody={
                  <p style={{ paddingTop: '1.5rem' }}>
                    Are you sure you want to remove this stateset?
                  </p>
                }
                accordionContent={getStates(s)}
                columns={[
                  <div
                    key={`name-${s.id}`}
                    className="ellipsed-text"
                  >
                    {s.name}
                  </div>,
                  <div
                    key={`type-${s.id}`}
                    className="ellipsed-text"
                  >
                    {s.isVirtualStateSet === true ? 'Virtual' : 'Component'}
                  </div>,
                  <div
                    key={`length-${s.id}`}
                    className="count-indicator"
                  >
                    {s?.states?.length || 1}
                  </div>,
                  '',
                  '',
                  '',
                  '',
                ]}
              />
            ))}
          </div>
        </div>
      </div>
      {showModal && (
        <SimpleModal
          title={formData.id ? `Update State set (${formData?.name})` : 'Add state set'}
          onClose={onCancel}
        >
          <div>
            <JSONEditor
              schema={schema}
              uiSchema={uiSchema(formData.id)}
              formData={formData || {}}
              customTransformErrors={customTransformErrors}
              saveButtonText={formData.id ? 'Update' : 'Create'}
              onFormChange={onFormChange}
              initialEditMode
              showEditButton={false}
              cancelCallback={onCancel}
              onFormSubmit={onSubmit}
              editorOnly
            />
          </div>
        </SimpleModal>
      )}
    </>
  );
});

export default StateSetView;
